<template>
	<v-autocomplete
		v-model="lang"
		:items="symbolsStore.languages"
		:item-value="(langCode: LanguageCode) => langCode"
		:item-title="(langCode: LanguageCode) => getLanguageName(langCode)"
		density="compact"
		variant="outlined"
		hide-details
		@update:model-value="emits('select', lang)"
	>
		<template #item="{ item, props }">
			<v-list-item v-bind="props" :title="null">
				<div class="d-flex justify-space-between">
					<div>{{ getLanguageName(item.raw) }}</div>
					<Flag class="ml-2" :lang="item.raw" :tooltip="true" />
				</div>
			</v-list-item>
		</template>
	</v-autocomplete>
</template>

<script lang="ts" setup>
import { onMounted, PropType, ref } from "vue";
import { useDomainStore } from "../store/domain";
import { useSymbolsStore } from "../store/symbols";
import type { LanguageCode } from "@lr-notitia/notitia-operator/schemas";
import Flag from "./Flag.vue";

const domainStore = useDomainStore();
const symbolsStore = useSymbolsStore();

const compProps = defineProps({
	lang: {
		type: String as PropType<LanguageCode>,
		required: true,
	},
});

const emits = defineEmits(["select"]);

const lang = ref<LanguageCode>(domainStore.languagePreference);

function getLanguageName(language: LanguageCode): string {
	return supportedLanguages[language] ?? language;
}

const supportedLanguages: { [key in LanguageCode]?: string } = {
	es: "Español",
	fr: "Français",
	sv: "Svenska",
	en: "English",
	de: "Deutsch",
	fi: "Suomi",
	hr: "Hrvatski",
	pl: "Polski",
	nl: "Nederlands",
	el: "Ελληνικά",
	id: "Bahasa Indonesia",
	mk: "Македонски",
	tr: "Türkçe",
	ta: "தமிழ்",
	th: "ไทย",
	ru: "Русский",
	ar: "العربية",
	ja: "日本語",
	da: "Dansk",
	ko: "한국어",
	zh: "中文",
	ur: "اردو",
	ro: "Română",
	hi: "हिन्दी",
	az: "Azərbaycan",
	bs: "Bosanski",
	sr: "Српски",
	it: "Italiano",
	cs: "Čeština",
	vi: "Tiếng Việt",
	lt: "Lietuvių",
	hu: "Magyar",
	no: "Norsk",
	nb: "Norsk bokmål",
	nn: "Norsk nynorsk",
	bg: "Български",
	ms: "Bahasa Melayu",
	sk: "Slovenčina",
	sl: "Slovenščina",
	lv: "Latviešu",
	uk: "Українська",
	fa: "فارسی",
	pt: "Português",
	et: "Eesti",
};

onMounted(() => (lang.value = compProps.lang));
</script>

<style scoped lang="scss"></style>
